
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface Activity {
  id: string;
  activity_type: 'Meeting' | 'Phone Call' | 'Email Communication' | 'Proposal Presentation' | 'Site Visit' | 'Follow-up' | 'Other';
  activity_date: string;
  title: string;
  notes?: string;
  contact_id?: string;
  performed_by_staff_id?: string;
  performed_by_partner_id?: string;
  created_at: string;
  updated_at: string;
  // Joined data
  contact?: {
    first_name: string;
    last_name: string;
    email: string;
    organization?: {
      name: string;
    };
  };
  staff?: {
    first_name: string;
    last_name: string;
    email: string;
  };
  partner?: {
    first_name: string;
    last_name: string;
    email: string;
  };
}

export interface CreateActivityData {
  activity_type: 'Meeting' | 'Phone Call' | 'Email Communication' | 'Proposal Presentation' | 'Site Visit' | 'Follow-up' | 'Other';
  activity_date: string;
  title: string;
  notes?: string;
  contact_id?: string;
  performed_by_staff_id?: string;
  performed_by_partner_id?: string;
}

export const useActivities = () => {
  return useQuery({
    queryKey: ["activities"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('crm_activities')
        .select(`
          *,
          contact:profiles!contact_id(first_name, last_name, email, organization:organizations(name)),
          staff:crm_staff!performed_by_staff_id(first_name, last_name, email),
          partner:crm_partners!performed_by_partner_id(first_name, last_name, email)
        `)
        .order('activity_date', { ascending: false });
      
      if (error) {
        console.error("Error fetching activities:", error);
        throw error;
      }
      return data as Activity[];
    }
  });
};

export const useActivitiesByContact = (contactId: string) => {
  return useQuery({
    queryKey: ["activities", "contact", contactId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('crm_activities')
        .select(`
          *,
          staff:crm_staff!performed_by_staff_id(first_name, last_name, email),
          partner:crm_partners!performed_by_partner_id(first_name, last_name, email)
        `)
        .eq('contact_id', contactId)
        .order('activity_date', { ascending: false });
      
      if (error) {
        console.error("Error fetching contact activities:", error);
        throw error;
      }
      return data as Activity[];
    },
    enabled: !!contactId
  });
};

export const useActivitiesByStaff = (staffId: string) => {
  return useQuery({
    queryKey: ["activities", "staff", staffId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('crm_activities')
        .select(`
          *,
          contact:profiles!contact_id(first_name, last_name, email)
        `)
        .eq('performed_by_staff_id', staffId)
        .order('activity_date', { ascending: false });
      
      if (error) {
        console.error("Error fetching staff activities:", error);
        throw error;
      }
      return data as Activity[];
    },
    enabled: !!staffId
  });
};

export const useActivitiesByPartner = (partnerId: string) => {
  return useQuery({
    queryKey: ["activities", "partner", partnerId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('crm_activities')
        .select(`
          *,
          contact:profiles!contact_id(first_name, last_name, email)
        `)
        .eq('performed_by_partner_id', partnerId)
        .order('activity_date', { ascending: false });
      
      if (error) {
        console.error("Error fetching partner activities:", error);
        throw error;
      }
      return data as Activity[];
    },
    enabled: !!partnerId
  });
};

export const useActivitiesByOrganization = (organizationId: string) => {
  return useQuery({
    queryKey: ['activities', 'organization', organizationId],
    queryFn: async () => {
      // First get all contacts for this organization
      const { data: contacts, error: contactsError } = await supabase
        .from('profiles')
        .select('id')
        .eq('organization_id', organizationId);

      if (contactsError) throw contactsError;

      if (!contacts || contacts.length === 0) {
        return [];
      }

      const contactIds = contacts.map(contact => contact.id);

      // Then get activities for those contacts
      const { data, error } = await supabase
        .from('crm_activities')
        .select(`
          *,
          contact:profiles!contact_id(first_name, last_name, email, organization_id),
          staff:crm_staff!performed_by_staff_id(first_name, last_name, email),
          partner:crm_partners!performed_by_partner_id(first_name, last_name, email)
        `)
        .in('contact_id', contactIds)
        .order('activity_date', { ascending: false });

      if (error) throw error;
      return data as Activity[];
    },
    enabled: !!organizationId,
  });
};

export const useCreateActivity = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: CreateActivityData) => {
      console.log("Creating activity with data:", data);
      
      const { data: result, error } = await supabase
        .from('crm_activities')
        .insert(data)
        .select()
        .single();
      
      if (error) {
        console.error("Insert Error:", error);
        throw new Error(error.message || "Failed to create activity");
      }
      
      console.log("Activity created:", result);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["activities"] });
      toast({
        title: "Success",
        description: "Activity created successfully",
      });
    },
    onError: (error) => {
      console.error("Create activity error:", error);
      toast({
        title: "Error",
        description: `Failed to create activity: ${error.message}`,
        variant: "destructive",
      });
    }
  });
};

export const useUpdateActivity = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<CreateActivityData> }) => {
      const { data: result, error } = await supabase
        .from('crm_activities')
        .update({ ...data, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();
      
      if (error) {
        console.error("Update Error:", error);
        throw new Error(error.message || "Failed to update activity");
      }
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["activities"] });
      toast({
        title: "Success",
        description: "Activity updated successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Failed to update activity: ${error.message}`,
        variant: "destructive",
      });
    }
  });
};

export const useDeleteActivity = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('crm_activities')
        .delete()
        .eq('id', id);
      
      if (error) {
        console.error("Delete Error:", error);
        throw new Error(error.message || "Failed to delete activity");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["activities"] });
      toast({
        title: "Success",
        description: "Activity deleted successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error", 
        description: `Failed to delete activity: ${error.message}`,
        variant: "destructive",
      });
    }
  });
};
